import {
    useEffect,
    useMemo,
    useState,
} from "react";
import {
    createPortal,
} from "react-dom";
import {
    ArrowDownLeft,
    ArrowUpRight,
    BadgeIndianRupee,
    ChevronLeft,
    ChevronRight,
    Coins,
    Download,
    Landmark,
    Search,
    ShieldCheck,
    Sparkles,
    Users,
    WalletCards,
    X,
} from "lucide-react";

import DateRangePicker from "../../../globalcomponents/DateRangePicker";
import { useAppDispatch, useAppSelector } from "../../../../Redux/hooks";
import {
    exportWalletTransactions,
    getWalletBalance,
    getWalletBalanceSummary,
    getWalletTransactions,
} from "../../../../Redux/wallet_apis/slice";
import {
    TransactionSource,
    TransactionStatus as ApiTxStatus,
    TransactionType as ApiTxType,
    UserType,
    WalletTransaction,
} from "../../../../Redux/wallet_apis/types";

import "./SalarWalletPage.css";

type TxType =
    | "Fund Transfer"
    | "Funds Received"
    | "Referral Incentives"
    | "Purchase"
    | "Refund"
    | "Admin"
    | "Payouts"
    | "Reversal";

type TxStatus =
    | "Success"
    | "Pending"
    | "Failed";

type BalanceCard = {
    id: string;
    code: string;
    title: string;
    value: string;
    suffix: string;
    icon:
    | "users"
    | "coins"
    | "rupee"
    | "landmark"
    | "sparkles";
};

type Party = {
    name: string;
    id: string;
};

type Transaction = {
    id: number;
    date: string;
    isoDate: string;
    transactionId: string;
    type: TxType;
    status: TxStatus;
    credit: number;
    debit: number;
    amount: number;
    balanceBefore: number;
    balanceAfter: number;
    referral: {
        name: string;
        milestone: string;
    } | null;
    tds: {
        gstAmount: number;
        percentage: number;
    } | null;
    beneficiary: Party | null;
    sender: Party | null;
};

type DateRange = {
    from: string;
    to: string;
};

type BalanceDef = {
    id: string;
    code: string;
    title: string;
    component: string;
    icon: BalanceCard["icon"];
    suffix?: string;
    isPoints?: boolean;
};

const BALANCE_DEFS: BalanceDef[] = [
    { id: "referral", code: "RI", title: "Referral Incentives", component: "REFERRAL_COMMISSION", icon: "users" },
    { id: "pearls", code: "SP", title: "Salar Pearls", component: "PEARLS_COMMISSION", icon: "coins", isPoints: true },
    { id: "flow", code: "SF", title: "Salar Flow Referral Incentives", component: "FLOW_COMMISSION", icon: "users" },
    { id: "unity", code: "SU", title: "Salar Unity Referral Incentive", component: "UNITY_COMMISSION", icon: "users" },
    { id: "samriddhi", code: "SS", title: "Salar Samriddhi Referral Incentive", component: "SAMRIDDHI_COMMISSION", icon: "sparkles" },
    { id: "milestone", code: "SM", title: "Salar Milestone Referral Incentive", component: "MILESTONE_REFERRAL_COMMISSION", icon: "sparkles" },
    { id: "partner", code: "PG", title: "Salar Partner Growth Incentive", component: "PARTNER_GROWTH_REFERRAL", icon: "users" },
    { id: "community", code: "CG", title: "Community Growth Incentive", component: "COMMUNITY_GROWTH_COMMISSION", icon: "users" },
    { id: "fusion", code: "FL", title: "Salar Fusion Incentive", component: "FUSION_LOCKED_COMMISSION", icon: "sparkles", suffix: "Locked" },
    { id: "coins", code: "SC", title: "Salar Coins", component: "SALAR_COINS", icon: "coins" },
    { id: "voucher", code: "SV", title: "Shopping Voucher", component: "SHOPPING_VOUCHER", icon: "rupee" },
    { id: "gifts", code: "GC", title: "Salar Gift Credits", component: "SALAR_GIFT_CREDITS", icon: "sparkles" },
    { id: "funds", code: "F", title: "Funds", component: "FUNDS", icon: "landmark" },
];

const COMPONENT_NAMES: Record<string, string> = {
    REFERRAL_COMMISSION: "Referral Incentive",
    LOYALTY_REFERRAL_COMMISSION: "Loyalty Referral Incentive",
    FLOW_COMMISSION: "Salar Flow Incentive",
    UNITY_COMMISSION: "Salar Unity Incentive",
    FUSION_LOCKED_COMMISSION: "Salar Fusion Incentive (Locked)",
    COMMUNITY_GROWTH_COMMISSION: "Community Growth Incentive",
    SAMRIDDHI_COMMISSION: "Salar Samriddhi Incentive",
    MILESTONE_REFERRAL_COMMISSION: "Salar Milestone Incentive",
    PEARLS_COMMISSION: "Salar Pearls",
    PARTNER_GROWTH_REFERRAL: "Salar Partner Growth Incentive",
    SICR1_COMMISSION: "Samriddhi R1 Incentive",
    SICR2_COMMISSION: "Samriddhi R2 Incentive",
    SICR3_COMMISSION: "Samriddhi R3 Incentive",
    SICR4_COMMISSION: "Samriddhi R4 Incentive",
    SALAR_COINS: "Salar Coins",
    SHOPPING_VOUCHER: "Shopping Voucher",
    SALAR_GIFT_CREDITS: "Salar Gift Credits",
    ADVERTISEMENT_CREDITS: "Advertisement Credits",
    DELIVERY_EARNINGS: "Delivery Earnings",
    FUNDS: "Funds",
};

const COMMISSION_COMPONENTS = new Set([
    "REFERRAL_COMMISSION",
    "LOYALTY_REFERRAL_COMMISSION",
    "FLOW_COMMISSION",
    "UNITY_COMMISSION",
    "FUSION_LOCKED_COMMISSION",
    "COMMUNITY_GROWTH_COMMISSION",
    "SAMRIDDHI_COMMISSION",
    "MILESTONE_REFERRAL_COMMISSION",
    "PEARLS_COMMISSION",
    "PARTNER_GROWTH_REFERRAL",
    "SICR1_COMMISSION",
    "SICR2_COMMISSION",
    "SICR3_COMMISSION",
    "SICR4_COMMISSION",
]);

// Maps the UI type filter to wallet transaction query params.
const TYPE_FILTER_QUERY: Record<
    "All" | TxType,
    { source?: TransactionSource; type?: ApiTxType; status?: ApiTxStatus }
> = {
    All: {},
    "Fund Transfer": { source: TransactionSource.TRANSFER, type: ApiTxType.DEBIT },
    "Funds Received": { source: TransactionSource.TRANSFER, type: ApiTxType.CREDIT },
    "Referral Incentives": { source: TransactionSource.COMMISSION },
    Purchase: { source: TransactionSource.PAYMENT, type: ApiTxType.DEBIT },
    Refund: { source: TransactionSource.REFUND },
    Admin: { source: TransactionSource.ADMIN },
    Payouts: { source: TransactionSource.WITHDRAWAL },
    Reversal: { status: ApiTxStatus.REVERSED },
};

const STATUS_FILTER_QUERY: Record<"All" | TxStatus, ApiTxStatus | undefined> = {
    All: undefined,
    Success: ApiTxStatus.COMPLETED,
    Pending: ApiTxStatus.PENDING,
    Failed: ApiTxStatus.FAILED,
};

const resolveCategory = (tx: WalletTransaction): TxType => {
    if (tx.source === TransactionSource.TRANSFER) {
        return tx.type === ApiTxType.CREDIT ? "Funds Received" : "Fund Transfer";
    }
    if (
        tx.source === TransactionSource.COMMISSION ||
        tx.source === TransactionSource.REWARD ||
        tx.source === TransactionSource.BONUS ||
        COMMISSION_COMPONENTS.has(tx.component)
    ) {
        return "Referral Incentives";
    }
    if (
        tx.status === ApiTxStatus.REVERSED ||
        tx.description?.toLowerCase().includes("revers")
    ) {
        return "Reversal";
    }
    if (tx.source === TransactionSource.REFUND) {
        return "Refund";
    }
    if (tx.source === TransactionSource.PAYMENT) {
        return tx.type === ApiTxType.CREDIT ? "Refund" : "Purchase";
    }
    if (tx.source === TransactionSource.WITHDRAWAL) {
        return "Payouts";
    }
    return "Admin";
};

const resolveStatus = (status: ApiTxStatus): TxStatus => {
    if (status === ApiTxStatus.COMPLETED) return "Success";
    if (status === ApiTxStatus.PENDING) return "Pending";
    return "Failed";
};

const toParty = (name?: string, id?: string): Party | null =>
    name || id ? { name: name || "—", id: id || "" } : null;

const resolveParties = (
    tx: WalletTransaction,
    category: TxType,
): { beneficiary: Party | null; sender: Party | null } => {
    const self =
        toParty(tx.customerName, tx.customerRegisterId) ?? { name: "You", id: "" };
    const counterparty = tx.counterparty
        ? toParty(
            tx.counterparty.name,
            tx.counterparty.registerId || tx.counterparty.userId,
        )
        : null;

    if (tx.source === TransactionSource.TRANSFER) {
        return tx.type === ApiTxType.DEBIT
            ? { beneficiary: counterparty, sender: self }
            : { beneficiary: self, sender: counterparty };
    }
    if (category === "Purchase") {
        return {
            beneficiary:
                toParty(tx.sellerName) ??
                toParty(tx.sourceService || "Merchant"),
            sender: self,
        };
    }
    if (category === "Payouts") {
        return { beneficiary: self, sender: { name: "Salar Payouts", id: "" } };
    }
    if (tx.type === ApiTxType.CREDIT) {
        return {
            beneficiary: self,
            sender: {
                name:
                    tx.sourceService ||
                    COMPONENT_NAMES[tx.component] ||
                    "Salar Wallet",
                id: "",
            },
        };
    }
    return { beneficiary: self, sender: self };
};

const money = (value: number) =>
    new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 2,
    }).format(value);

const BalanceIcon = ({
    icon,
}: {
    icon: BalanceCard["icon"];
}) => {
    const common = {
        size: 18,
        strokeWidth: 2,
    };

    if (icon === "users") {
        return <Users {...common} />;
    }

    if (icon === "coins") {
        return <Coins {...common} />;
    }

    if (icon === "rupee") {
        return <BadgeIndianRupee {...common} />;
    }

    if (icon === "landmark") {
        return <Landmark {...common} />;
    }

    return <Sparkles {...common} />;
};

const TxStatusBadge = ({
    status,
}: {
    status: TxStatus;
}) => (
    <span
        className={`salar-wallet-status salar-wallet-status-${status.toLowerCase()}`}
    >
        {status}
    </span>
);

const TxTypeBadge = ({
    type,
}: {
    type: TxType;
}) => (
    <span
        className={`salar-wallet-type salar-wallet-type-${type
            .toLowerCase()
            .replace(/\s+/g, "-")}`}
    >
        {type}
    </span>
);

const PartyInfo = ({
    label,
    party,
}: {
    label: string;
    party: Party | null;
}) => (
    <div className="salar-wallet-party">
        <span>{label}</span>

        {party ? (
            <>
                <strong>{party.name}</strong>
                <small>{party.id}</small>
            </>
        ) : (
            <strong>—</strong>
        )}
    </div>
);

const MobileMovement = ({
    transaction,
}: {
    transaction: Transaction;
}) => {
    if (transaction.credit > 0) {
        return (
            <div className="salar-wallet-mobile-movement is-credit">
                <ArrowDownLeft size={16} />
                <div>
                    <span>Credited</span>
                    <strong>{money(transaction.credit)}</strong>
                </div>
            </div>
        );
    }

    if (transaction.debit > 0) {
        return (
            <div className="salar-wallet-mobile-movement is-debit">
                <ArrowUpRight size={16} />
                <div>
                    <span>Debited</span>
                    <strong>{money(transaction.debit)}</strong>
                </div>
            </div>
        );
    }

    return (
        <div className="salar-wallet-mobile-movement is-neutral">
            <WalletCards size={16} />
            <div>
                <span>Movement</span>
                <strong>No change</strong>
            </div>
        </div>
    );
};

const SalarWalletPage = () => {
    const dispatch = useAppDispatch();

    const {
        balance,
        summary,
        transactions: apiTransactions,
        pagination,
        loading,
        error,
    } = useAppSelector((state) => state.wallet);

    const { user } = useAppSelector(
        (state) => state.customerAuth
    );

    const [entries, setEntries] = useState(10);
    const [search, setSearch] = useState("");

    const [typeFilter, setTypeFilter] =
        useState<"All" | TxType>("All");

    const [statusFilter, setStatusFilter] =
        useState<"All" | TxStatus>("All");

    const [dateRange, setDateRange] =
        useState<DateRange>({
            from: "",
            to: "",
        });

    const [page, setPage] = useState(1);

    const [earningsOpen, setEarningsOpen] =
        useState(false);

    const [selectedTransaction, setSelectedTransaction] =
        useState<Transaction | null>(null);

    const [exporting, setExporting] =
        useState(false);

    useEffect(() => {
        if (user?._id) {
            dispatch(getWalletBalance());
            dispatch(getWalletBalanceSummary());
        }
    }, [dispatch, user?._id]);

    useEffect(() => {
        if (!user?._id) {
            return;
        }

        if (dateRange.from && !dateRange.to) {
            return;
        }

        const typeQuery = TYPE_FILTER_QUERY[typeFilter];

        dispatch(
            getWalletTransactions({
                page,
                limit: entries,
                sortOrder: "DESC",
                userType: UserType.CUSTOMER,
                source: typeQuery.source,
                type: typeQuery.type,
                status:
                    typeQuery.status ??
                    STATUS_FILTER_QUERY[statusFilter],
                startDate:
                    dateRange.from || undefined,
                endDate:
                    dateRange.to || undefined,
            })
        );
    }, [
        dispatch,
        user?._id,
        page,
        entries,
        typeFilter,
        statusFilter,
        dateRange.from,
        dateRange.to,
    ]);

    useEffect(() => {
        const sheetOpen =
            earningsOpen ||
            Boolean(selectedTransaction);

        if (!sheetOpen) {
            return;
        }

        const previousOverflow =
            document.body.style.overflow;

        document.body.style.overflow =
            "hidden";

        /*
         * The UserLayout mobile bottom navigation is a fixed layer.
         * Add a body state while this wallet sheet is open so CSS can
         * force that navigation behind the offcanvas.
         */
        document.body.classList.add(
            "salar-wallet-sheet-open"
        );

        const handleEscape = (
            event: KeyboardEvent
        ) => {
            if (event.key === "Escape") {
                setEarningsOpen(false);
                setSelectedTransaction(null);
            }
        };

        document.addEventListener(
            "keydown",
            handleEscape
        );

        return () => {
            document.body.style.overflow =
                previousOverflow;

            document.body.classList.remove(
                "salar-wallet-sheet-open"
            );

            document.removeEventListener(
                "keydown",
                handleEscape
            );
        };
    }, [
        earningsOpen,
        selectedTransaction,
    ]);

    const findComponentBalance = (
        component: string
    ): number => {
        if (balance?.components) {
            const found = balance.components.find(
                (item) =>
                    item.component === component
            );
            if (found) {
                return found.balance;
            }
        }

        const commission =
            balance?.commissionBalances as
                | Record<string, number | undefined>
                | undefined;

        const payment =
            balance?.paymentBalances as
                | Record<string, number | undefined>
                | undefined;

        const lookup: Record<
            string,
            number | undefined
        > = {
            REFERRAL_COMMISSION:
                commission?.referralCommission,
            FLOW_COMMISSION:
                commission?.flowCommission,
            UNITY_COMMISSION:
                commission?.unityCommission,
            FUSION_LOCKED_COMMISSION:
                commission?.fusionLockedCommission,
            COMMUNITY_GROWTH_COMMISSION:
                commission?.communityGrowthCommission,
            SAMRIDDHI_COMMISSION:
                commission?.samriddhiCommission,
            MILESTONE_REFERRAL_COMMISSION:
                commission?.milestoneReferralCommission,
            PEARLS_COMMISSION:
                commission?.pearlsCommission,
            PARTNER_GROWTH_REFERRAL:
                commission?.partnerGrowthReferral,
            SALAR_COINS: payment?.salarCoins,
            SHOPPING_VOUCHER:
                payment?.shoppingVoucher ??
                balance?.shoppingVoucher,
            SALAR_GIFT_CREDITS:
                payment?.salarGiftCredits,
            FUNDS: balance?.funds,
        };

        return lookup[component] ?? 0;
    };

    const balances: BalanceCard[] =
        useMemo(
            () =>
                BALANCE_DEFS.map((def) => {
                    const value =
                        findComponentBalance(
                            def.component
                        );

                    return {
                        id: def.id,
                        code: def.code,
                        title: def.title,
                        value: def.isPoints
                            ? new Intl.NumberFormat(
                                "en-IN"
                            ).format(value)
                            : money(value),
                        suffix:
                            def.suffix ??
                            (def.isPoints
                                ? "Points"
                                : "Available"),
                        icon: def.icon,
                    };
                }),
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [balance]
        );

    const mappedTransactions: Transaction[] =
        useMemo(() => {
            const offset =
                ((pagination?.page ?? 1) - 1) *
                (pagination?.limit ?? entries);

            return apiTransactions.map(
                (tx, index) => {
                    const category =
                        resolveCategory(tx);

                    const meta = (tx.metadata ??
                        {}) as Record<
                        string,
                        unknown
                    >;

                    const level =
                        meta.level ?? meta.levelNo;

                    const milestone =
                        ((
                            meta.milestone ??
                            meta.period ??
                            meta.month
                        ) as string | undefined) ??
                        (level !== undefined
                            ? `Level ${level}`
                            : undefined) ??
                        (tx.orderNumber
                            ? `Order ${tx.orderNumber}`
                            : undefined);

                    const isIncentive =
                        category ===
                        "Referral Incentives" ||
                        (tx.deductions?.grossAmount ??
                            0) > 0;

                    const referral = isIncentive
                        ? {
                            name:
                                (meta.commissionSource as
                                    | string
                                    | undefined) ||
                                COMPONENT_NAMES[
                                tx.component
                                ] ||
                                "Incentive",
                            milestone:
                                milestone ?? "—",
                        }
                        : null;

                    const deductions =
                        tx.deductions;

                    const tds =
                        deductions &&
                            (deductions.tdsAmount > 0 ||
                                deductions.adminFeeAmount >
                                0)
                            ? {
                                gstAmount:
                                    deductions.tdsAmount >
                                        0
                                        ? deductions.tdsAmount
                                        : deductions.adminFeeAmount,
                                percentage:
                                    deductions.tdsPercentage >
                                        0
                                        ? deductions.tdsPercentage
                                        : deductions.adminFeePercentage,
                            }
                            : null;

                    const parties = resolveParties(
                        tx,
                        category
                    );

                    const created = new Date(
                        tx.createdAt
                    );

                    const day = String(
                        created.getDate()
                    ).padStart(2, "0");

                    const month = String(
                        created.getMonth() + 1
                    ).padStart(2, "0");

                    const year =
                        created.getFullYear();

                    return {
                        id: offset + index + 1,
                        date: `${day}-${month}-${year}`,
                        isoDate: created
                            .toISOString()
                            .slice(0, 10),
                        transactionId:
                            tx.transactionNumber,
                        type: category,
                        status: resolveStatus(
                            tx.status
                        ),
                        credit:
                            tx.type ===
                                ApiTxType.CREDIT
                                ? Number(tx.amount)
                                : 0,
                        debit:
                            tx.type ===
                                ApiTxType.DEBIT
                                ? Number(tx.amount)
                                : 0,
                        amount: Number(tx.amount),
                        balanceBefore: Number(
                            tx.balanceBefore
                        ),
                        balanceAfter: Number(
                            tx.balanceAfter
                        ),
                        referral,
                        tds,
                        beneficiary:
                            parties.beneficiary,
                        sender: parties.sender,
                    };
                }
            );
        }, [
            apiTransactions,
            pagination,
            entries,
        ]);

    const visible =
        useMemo(() => {
            const query =
                search
                    .trim()
                    .toLowerCase();

            if (!query) {
                return mappedTransactions;
            }

            return mappedTransactions.filter(
                (item) =>
                    [
                        item.transactionId,
                        item.type,
                        item.status,
                        item.referral?.name,
                        item.referral?.milestone,
                        item.beneficiary?.name,
                        item.beneficiary?.id,
                        item.sender?.name,
                        item.sender?.id,
                    ]
                        .filter(Boolean)
                        .some((value) =>
                            String(value)
                                .toLowerCase()
                                .includes(query)
                        )
            );
        }, [
            mappedTransactions,
            search,
        ]);

    const totalPages =
        Math.max(
            1,
            pagination?.totalPages ?? 1
        );

    const safePage =
        Math.min(
            page,
            totalPages
        );

    const totalCount =
        pagination?.total ??
        mappedTransactions.length;

    const resetPage = () =>
        setPage(1);

    const closeSheets = () => {
        setEarningsOpen(false);
        setSelectedTransaction(null);
    };

    const handleExport = async () => {
        const typeQuery =
            TYPE_FILTER_QUERY[typeFilter];

        setExporting(true);

        try {
            const action = await dispatch(
                exportWalletTransactions({
                    userType: UserType.CUSTOMER,
                    sortOrder: "DESC",
                    source: typeQuery.source,
                    type: typeQuery.type,
                    status:
                        typeQuery.status ??
                        STATUS_FILTER_QUERY[
                        statusFilter
                        ],
                    startDate:
                        dateRange.from ||
                        undefined,
                    endDate:
                        dateRange.to ||
                        undefined,
                })
            );

            if (
                exportWalletTransactions.fulfilled.match(
                    action
                )
            ) {
                const {
                    content,
                    filename,
                } = action.payload.data;

                const blob = new Blob(
                    [content],
                    {
                        type: "text/csv;charset=utf-8;",
                    }
                );

                const url =
                    URL.createObjectURL(blob);

                const link =
                    document.createElement(
                        "a"
                    );

                link.href = url;
                link.download = filename;
                link.click();

                URL.revokeObjectURL(url);
            }
        } finally {
            setExporting(false);
        }
    };

    return (
        <section className="salar-wallet-page">
            <header className="salar-wallet-hero">
                <div className="salar-wallet-hero-icon">
                    <WalletCards size={24} />
                </div>

                <div className="salar-wallet-hero-copy">
                    <span className="salar-wallet-eyebrow">
                        MY WALLET
                    </span>

                    <h1>Salar Wallet</h1>

                    <p>
                        Incentive balances, credits, debits and complete wallet transaction history.
                    </p>
                </div>

                <div className="salar-wallet-total">
                    <span>
                        Available Balance
                    </span>

                    <strong>
                        {money(
                            summary?.availableBalance ??
                            balance?.availableBalance ??
                            balance?.totalAvailable ??
                            0
                        )}
                    </strong>

                    <small>
                        <ShieldCheck size={14} />
                        {summary?.status === "ACTIVE"
                            ? "Active wallet"
                            : summary?.status ??
                            "Wallet"}
                    </small>

                    {(balance?.lockedBalance ?? 0) > 0 && (
                        <small title="Reserved for pending auto-repurchase / upgrade orders">
                            Frozen: {money(balance?.lockedBalance ?? 0)}
                        </small>
                    )}
                </div>
            </header>

            <section className="salar-wallet-section salar-wallet-earnings-section">
                <div className="salar-wallet-section-head salar-wallet-earnings-head">
                    <div>
                        <span className="salar-wallet-eyebrow">
                            INCENTIVE BALANCES
                        </span>

                        <h2>
                            Earning sources
                        </h2>
                    </div>

                    <span className="salar-wallet-count">
                        {balances.length} balances
                    </span>
                </div>

                <div className="salar-wallet-balance-grid">
                    {balances.map(
                        (
                            item,
                            index
                        ) => (
                            <article
                                className={`salar-wallet-balance-card accent-${index % 5}`}
                                key={item.id}
                            >
                                <div className="salar-wallet-balance-main">
                                    <span className="salar-wallet-code">
                                        {item.code}
                                    </span>

                                    <div className="salar-wallet-balance-copy">
                                        <h3>
                                            {item.title}
                                        </h3>

                                        <div className="salar-wallet-balance-value">
                                            <strong>
                                                {item.value}
                                            </strong>

                                            <span>
                                                {item.suffix}
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                <span className="salar-wallet-balance-icon">
                                    <BalanceIcon
                                        icon={item.icon}
                                    />
                                </span>
                            </article>
                        )
                    )}
                </div>

                <button
                    type="button"
                    className="salar-wallet-mobile-earnings-button"
                    onClick={() =>
                        setEarningsOpen(true)
                    }
                >
                    <span className="salar-wallet-mobile-earnings-icon">
                        <BadgeIndianRupee size={20} />
                    </span>

                    <span className="salar-wallet-mobile-earnings-copy">
                        <strong>
                            Earnings
                        </strong>

                        <small>
                            {balances.length} earning sources
                        </small>
                    </span>

                    <span className="salar-wallet-mobile-earnings-action">
                        View balances
                        <ChevronRight size={17} />
                    </span>
                </button>
            </section>

            <section className="salar-wallet-section salar-wallet-ledger">
                <div className="salar-wallet-section-head">
                    <div>
                        <span className="salar-wallet-eyebrow">
                            WALLET LEDGER
                        </span>

                        <h2>
                            Transactions
                        </h2>

                        <p>
                            Credit, debit, balances, incentive details, tax and party information.
                        </p>
                    </div>

                    <span className="salar-wallet-count">
                        {totalCount} transactions
                    </span>
                </div>

                <div className="salar-wallet-toolbar">
                    <label className="salar-wallet-search">
                        <Search size={18} />

                        <input
                            type="search"
                            value={search}
                            placeholder="Search transactions..."
                            onChange={(event) => {
                                setSearch(
                                    event.target.value
                                );
                                resetPage();
                            }}
                        />
                    </label>

                    <select
                        className="salar-wallet-filter salar-wallet-entries"
                        value={entries}
                        onChange={(event) => {
                            setEntries(
                                Number(
                                    event.target.value
                                )
                            );
                            resetPage();
                        }}
                        aria-label="Entries per page"
                    >
                        <option value={5}>
                            5 Entries
                        </option>

                        <option value={10}>
                            10 Entries
                        </option>

                        <option value={20}>
                            20 Entries
                        </option>
                    </select>

                    <select
                        className="salar-wallet-filter salar-wallet-type-filter"
                        value={typeFilter}
                        onChange={(event) => {
                            setTypeFilter(
                                event.target.value as
                                | "All"
                                | TxType
                            );
                            resetPage();
                        }}
                        aria-label="Transaction type"
                    >
                        <option value="All">
                            All Types
                        </option>

                        <option value="Fund Transfer">
                            Fund Transfer
                        </option>

                        <option value="Funds Received">
                            Funds Received
                        </option>

                        <option value="Referral Incentives">
                            Referral Incentives
                        </option>

                        <option value="Purchase">
                            Purchase
                        </option>

                        <option value="Refund">
                            Refund
                        </option>

                        <option value="Admin">
                            Admin
                        </option>

                        <option value="Payouts">
                            Payouts
                        </option>

                        <option value="Reversal">
                            Reversal
                        </option>
                    </select>

                    <select
                        className="salar-wallet-filter salar-wallet-status-filter"
                        value={statusFilter}
                        onChange={(event) => {
                            setStatusFilter(
                                event.target.value as
                                | "All"
                                | TxStatus
                            );
                            resetPage();
                        }}
                        aria-label="Transaction status"
                    >
                        <option value="All">
                            All Status
                        </option>

                        <option value="Success">
                            Success
                        </option>

                        <option value="Pending">
                            Pending
                        </option>

                        <option value="Failed">
                            Failed
                        </option>
                    </select>

                    <div className="salar-wallet-date-field">
                        <DateRangePicker
                            value={dateRange}
                            onChange={(value) => {
                                setDateRange(value);
                                resetPage();
                            }}
                        />
                    </div>

                    <button
                        type="button"
                        className="salar-wallet-filter salar-wallet-export"
                        onClick={handleExport}
                        disabled={exporting}
                    >
                        <Download size={16} />
                        {exporting
                            ? "Exporting..."
                            : "Export"}
                    </button>
                </div>

                {error && (
                    <div
                        className="alert alert-danger text-white py-2 fs-7"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                <div className="salar-wallet-table-wrap">
                    <table className="salar-wallet-table">
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Date</th>
                                <th>Transaction ID</th>
                                <th>Type</th>
                                <th>Status</th>
                                <th>Credit</th>
                                <th>Debit</th>
                                <th>Amount</th>
                                <th>Balance Before</th>
                                <th>Balance After</th>
                                <th>Referral Incentive Details</th>
                                <th>TDS</th>
                                <th>Beneficiary Details</th>
                                <th>Sender Details</th>
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={14}>
                                        <div className="salar-wallet-empty">
                                            Loading transactions...
                                        </div>
                                    </td>
                                </tr>
                            ) : visible.length ? (
                                visible.map(
                                    (item) => (
                                        <tr
                                            key={
                                                item.transactionId
                                            }
                                        >
                                            <td className="salar-wallet-sno">
                                                {item.id}
                                            </td>

                                            <td className="salar-wallet-nowrap">
                                                {item.date}
                                            </td>

                                            <td>
                                                <span className="salar-wallet-txn-id">
                                                    {item.transactionId}
                                                </span>
                                            </td>

                                            <td>
                                                <TxTypeBadge
                                                    type={item.type}
                                                />
                                            </td>

                                            <td>
                                                <TxStatusBadge
                                                    status={item.status}
                                                />
                                            </td>

                                            <td>
                                                <span
                                                    className={`salar-wallet-money credit ${item.credit > 0
                                                        ? "active"
                                                        : ""
                                                        }`}
                                                >
                                                    <ArrowDownLeft size={16} />

                                                    {item.credit > 0
                                                        ? money(item.credit)
                                                        : "—"}
                                                </span>
                                            </td>

                                            <td>
                                                <span
                                                    className={`salar-wallet-money debit ${item.debit > 0
                                                        ? "active"
                                                        : ""
                                                        }`}
                                                >
                                                    <ArrowUpRight size={16} />

                                                    {item.debit > 0
                                                        ? money(item.debit)
                                                        : "—"}
                                                </span>
                                            </td>

                                            <td>
                                                <strong className="salar-wallet-amount">
                                                    {money(item.amount)}
                                                </strong>
                                            </td>

                                            <td className="salar-wallet-nowrap">
                                                {money(item.balanceBefore)}
                                            </td>

                                            <td className="salar-wallet-nowrap salar-wallet-after">
                                                {money(item.balanceAfter)}
                                            </td>

                                            <td>
                                                {item.referral ? (
                                                    <div className="salar-wallet-stack">
                                                        <strong>
                                                            {item.referral.name}
                                                        </strong>

                                                        <span>
                                                            {item.referral.milestone}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="salar-wallet-muted">
                                                        Not applicable
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                {item.tds ? (
                                                    <div className="salar-wallet-stack">
                                                        <strong>
                                                            GST {money(item.tds.gstAmount)}
                                                        </strong>

                                                        <span>
                                                            {item.tds.percentage}%
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <span className="salar-wallet-muted">
                                                        —
                                                    </span>
                                                )}
                                            </td>

                                            <td>
                                                <PartyInfo
                                                    label="Beneficiary"
                                                    party={item.beneficiary}
                                                />
                                            </td>

                                            <td>
                                                <PartyInfo
                                                    label="Sender"
                                                    party={item.sender}
                                                />
                                            </td>
                                        </tr>
                                    )
                                )
                            ) : (
                                <tr>
                                    <td colSpan={14}>
                                        <div className="salar-wallet-empty">
                                            No transactions match the selected filters.
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="salar-wallet-mobile-list">
                    {loading ? (
                        <div className="salar-wallet-empty">
                            Loading transactions...
                        </div>
                    ) : visible.length ? (
                        visible.map(
                            (item) => (
                                <button
                                    type="button"
                                    className="salar-wallet-mobile-card"
                                    key={`mobile-${item.transactionId}`}
                                    onClick={() =>
                                        setSelectedTransaction(item)
                                    }
                                >
                                    <div className="salar-wallet-mobile-card-top">
                                        <div className="salar-wallet-mobile-state">
                                            <span className="salar-wallet-mobile-index">
                                                {item.id}
                                            </span>

                                            <TxStatusBadge
                                                status={item.status}
                                            />
                                        </div>

                                        <span className="salar-wallet-mobile-date">
                                            {item.date}
                                        </span>
                                    </div>

                                    <div className="salar-wallet-mobile-card-main">
                                        <span className="salar-wallet-mobile-type-icon">
                                            <WalletCards size={18} />
                                        </span>

                                        <span className="salar-wallet-mobile-card-copy">
                                            <strong>
                                                {item.type}
                                            </strong>

                                            <small>
                                                {item.transactionId}
                                            </small>
                                        </span>

                                        <ChevronRight
                                            size={19}
                                            className="salar-wallet-mobile-chevron"
                                        />
                                    </div>

                                    <div className="salar-wallet-mobile-card-bottom">
                                        <div>
                                            <span>Amount</span>
                                            <strong>
                                                {money(item.amount)}
                                            </strong>
                                        </div>

                                        <MobileMovement
                                            transaction={item}
                                        />
                                    </div>
                                </button>
                            )
                        )
                    ) : (
                        <div className="salar-wallet-empty">
                            No transactions match the selected filters.
                        </div>
                    )}
                </div>

                <div className="salar-wallet-pagination">
                    <button
                        type="button"
                        disabled={safePage <= 1}
                        onClick={() =>
                            setPage((old) =>
                                Math.max(
                                    1,
                                    old - 1
                                )
                            )
                        }
                        aria-label="Previous page"
                    >
                        <ChevronLeft size={19} />
                    </button>

                    <span>
                        Page {safePage} of {totalPages}
                    </span>

                    <button
                        type="button"
                        disabled={
                            safePage >=
                            totalPages
                        }
                        onClick={() =>
                            setPage((old) =>
                                Math.min(
                                    totalPages,
                                    old + 1
                                )
                            )
                        }
                        aria-label="Next page"
                    >
                        <ChevronRight size={19} />
                    </button>
                </div>
            </section>

            {earningsOpen &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className="salar-wallet-mobile-sheet-backdrop"
                        onClick={closeSheets}
                        role="presentation"
                    >
                        <aside
                            className="salar-wallet-mobile-sheet salar-wallet-earnings-sheet"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Earning balances"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <div className="salar-wallet-sheet-handle" />

                            <div className="salar-wallet-sheet-head">
                                <div>
                                    <span className="salar-wallet-eyebrow">
                                        WALLET EARNINGS
                                    </span>

                                    <h3>
                                        Earning balances
                                    </h3>

                                    <p>
                                        All available incentive balances in one place.
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="salar-wallet-sheet-close"
                                    onClick={closeSheets}
                                    aria-label="Close earning balances"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="salar-wallet-sheet-balance-list">
                                {balances.map(
                                    (
                                        item,
                                        index
                                    ) => (
                                        <div
                                            className={`salar-wallet-sheet-balance-row accent-${index % 5}`}
                                            key={`sheet-${item.id}`}
                                        >
                                            <span className="salar-wallet-sheet-balance-code">
                                                {item.code}
                                            </span>

                                            <span className="salar-wallet-sheet-balance-title">
                                                <strong>
                                                    {item.title}
                                                </strong>

                                                <small>
                                                    {item.suffix}
                                                </small>
                                            </span>

                                            <strong className="salar-wallet-sheet-balance-value">
                                                {item.value}
                                            </strong>
                                        </div>
                                    )
                                )}
                            </div>
                        </aside>
                    </div>,
                    document.body
                )}

            {selectedTransaction &&
                typeof document !== "undefined" &&
                createPortal(
                    <div
                        className="salar-wallet-mobile-sheet-backdrop"
                        onClick={closeSheets}
                        role="presentation"
                    >
                        <aside
                            className="salar-wallet-mobile-sheet salar-wallet-transaction-sheet"
                            role="dialog"
                            aria-modal="true"
                            aria-label="Transaction details"
                            onClick={(event) =>
                                event.stopPropagation()
                            }
                        >
                            <div className="salar-wallet-sheet-handle" />

                            <div className="salar-wallet-sheet-head">
                                <div>
                                    <span className="salar-wallet-eyebrow">
                                        TRANSACTION DETAILS
                                    </span>

                                    <h3>
                                        {selectedTransaction.type}
                                    </h3>

                                    <p>
                                        {selectedTransaction.transactionId}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    className="salar-wallet-sheet-close"
                                    onClick={closeSheets}
                                    aria-label="Close transaction details"
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            <div className="salar-wallet-sheet-status-row">
                                <TxStatusBadge
                                    status={selectedTransaction.status}
                                />

                                <span>
                                    {selectedTransaction.date}
                                </span>
                            </div>

                            <div className="salar-wallet-sheet-summary">
                                <div className="salar-wallet-sheet-summary-amount">
                                    <span>
                                        Transaction amount
                                    </span>

                                    <strong>
                                        {money(selectedTransaction.amount)}
                                    </strong>
                                </div>

                                <MobileMovement
                                    transaction={selectedTransaction}
                                />
                            </div>

                            <div className="salar-wallet-sheet-detail-grid">
                                <div className="salar-wallet-sheet-detail">
                                    <span>
                                        Balance Before
                                    </span>

                                    <strong>
                                        {money(
                                            selectedTransaction.balanceBefore
                                        )}
                                    </strong>
                                </div>

                                <div className="salar-wallet-sheet-detail">
                                    <span>
                                        Balance After
                                    </span>

                                    <strong>
                                        {money(
                                            selectedTransaction.balanceAfter
                                        )}
                                    </strong>
                                </div>

                                <div className="salar-wallet-sheet-detail">
                                    <span>
                                        Credit
                                    </span>

                                    <strong className="is-credit-text">
                                        {selectedTransaction.credit > 0
                                            ? money(selectedTransaction.credit)
                                            : "—"}
                                    </strong>
                                </div>

                                <div className="salar-wallet-sheet-detail">
                                    <span>
                                        Debit
                                    </span>

                                    <strong className="is-debit-text">
                                        {selectedTransaction.debit > 0
                                            ? money(selectedTransaction.debit)
                                            : "—"}
                                    </strong>
                                </div>
                            </div>

                            <div className="salar-wallet-sheet-section">
                                <span className="salar-wallet-sheet-section-title">
                                    Referral Incentive Details
                                </span>

                                <div className="salar-wallet-sheet-wide-detail">
                                    <span>
                                        Incentive
                                    </span>

                                    <strong>
                                        {selectedTransaction.referral?.name ??
                                            "Not applicable"}
                                    </strong>

                                    <small>
                                        {selectedTransaction.referral?.milestone ??
                                            "—"}
                                    </small>
                                </div>
                            </div>

                            <div className="salar-wallet-sheet-section">
                                <span className="salar-wallet-sheet-section-title">
                                    TDS / GST
                                </span>

                                <div className="salar-wallet-sheet-detail-grid">
                                    <div className="salar-wallet-sheet-detail">
                                        <span>
                                            GST Amount
                                        </span>

                                        <strong>
                                            {selectedTransaction.tds
                                                ? money(
                                                    selectedTransaction.tds
                                                        .gstAmount
                                                )
                                                : "—"}
                                        </strong>
                                    </div>

                                    <div className="salar-wallet-sheet-detail">
                                        <span>
                                            Percentage
                                        </span>

                                        <strong>
                                            {selectedTransaction.tds
                                                ? `${selectedTransaction.tds.percentage}%`
                                                : "—"}
                                        </strong>
                                    </div>
                                </div>
                            </div>

                            <div className="salar-wallet-sheet-section">
                                <span className="salar-wallet-sheet-section-title">
                                    Party Details
                                </span>

                                <div className="salar-wallet-sheet-party-grid">
                                    <PartyInfo
                                        label="Beneficiary"
                                        party={
                                            selectedTransaction.beneficiary
                                        }
                                    />

                                    <PartyInfo
                                        label="Sender"
                                        party={
                                            selectedTransaction.sender
                                        }
                                    />
                                </div>
                            </div>
                        </aside>
                    </div>,
                    document.body
                )}
        </section>
    );
};

export default SalarWalletPage;
